import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, ExternalLink, HandCoins, Wallet } from "lucide-react";
import { formatMoney, type Settlement } from "@travelix/core";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MoneyText } from "@/components/money-text";
import { cn } from "@/lib/utils";

/**
 * One "who owes who" line. From your side it reads "You owe Bob" /
 * "Bob owes you"; between others (group view) "Carol · Owes Bob". The settle
 * action lives on the row itself.
 */
export function DebtRow({
  debt,
  colorSeed,
  currency,
  currentUid,
  tripId,
  readOnly,
}: {
  debt: Settlement;
  colorSeed: number;
  currency: string;
  currentUid: string | null;
  tripId: string;
  readOnly: boolean;
}) {
  const youOwe = debt.fromUserId === currentUid;
  const owedToYou = debt.toUserId === currentUid;
  const other = youOwe ? debt.toName : owedToYou ? debt.fromName : null;
  const title = other ?? debt.fromName;
  const status = youOwe ? "You owe" : owedToYou ? "Owes you" : `Owes ${debt.toName}`;
  return (
    <li className="flex items-center gap-3 p-3">
      <Avatar name={title} colorSeed={colorSeed} size="md" />
      <div className="flex-1 min-w-0">
        <p className="text-[15px] font-semibold text-ink m-0 truncate">{title}</p>
        <p className={cn("text-[12px] m-0", owedToYou ? "text-success" : "text-ink-muted")}>
          {status}
        </p>
      </div>
      <MoneyText
        amount={debt.amount}
        currency={currency}
        className={cn("text-[15px] font-semibold", owedToYou ? "text-success" : "text-ink")}
      />
      {!readOnly ? (
        <SettleAction debt={debt} currency={currency} currentUid={currentUid} tripId={tripId} />
      ) : null}
    </li>
  );
}

/**
 * Per-row settle action for the "who owes who" card.
 *
 * - You owe → "Pay": popover with Venmo / Bizum / PayPal deep-links, copy
 *   helpers, and "Record payment" to log what was actually handed over.
 * - They owe you → "Mark paid": straight to the record-payment sheet.
 * - Between two other travelers (group view) → "Record".
 *
 * Record-payment is prefilled with the debt but editable, since the common
 * case is a partial payment. We never move money ourselves.
 */
export function SettleAction({
  debt,
  currency,
  currentUid,
  tripId,
}: {
  debt: Settlement;
  currency: string;
  currentUid: string | null;
  tripId: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const youPay = currentUid === debt.fromUserId;
  const youReceive = currentUid === debt.toUserId;

  if (!youPay) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => navigate(recordPaymentUrl(tripId, debt))}
        aria-label={`Record ${debt.fromName} paying ${debt.toName}`}
        className="shrink-0 text-primary hover:bg-primary-soft"
      >
        {youReceive ? "Mark paid" : "Record"}
      </Button>
    );
  }

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Pay ${debt.toName}`}
        className="text-primary hover:bg-primary-soft"
      >
        Pay
      </Button>
      {open ? (
        <SettleActions
          settlement={debt}
          currency={currency}
          tripId={tripId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}

function recordPaymentUrl(tripId: string, s: Settlement): string {
  return (
    `/trip/${tripId}/record-payment` +
    `?from=${encodeURIComponent(s.fromUserId)}` +
    `&to=${encodeURIComponent(s.toUserId)}` +
    `&amount=${s.amount.toFixed(2)}`
  );
}

// ─── Popover with payment-app links + copy ──────────────────
function SettleActions({
  settlement,
  currency,
  tripId,
  onClose,
}: {
  settlement: Settlement;
  currency: string;
  tripId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const amount = settlement.amount.toFixed(2);
  const money = formatMoney(settlement.amount, currency);
  const note = `Travelix · ${settlement.fromName} → ${settlement.toName}`;

  const recordUrl = recordPaymentUrl(tripId, settlement);

  // Deep links. These open the app if installed; on desktop / when the app
  // isn't there, they land on the web fallback the platform provides.
  const venmoUrl = `https://venmo.com/?txn=pay&note=${encodeURIComponent(note)}&amount=${amount}`;
  const bizumUrl = `https://bizum.es/`; // Bizum has no public deep-link scheme; open marketing site
  const paypalUrl = `https://www.paypal.com/qrcodes/managed/send?amount=${amount}&currency=${currency}`;

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Couldn't copy — try selecting manually.");
    }
  };

  const openLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Payment options"
      className={cn(
        "absolute right-0 top-full z-20 mt-1 w-64 origin-top-right rounded-xl",
        "border border-line bg-bg-elevated shadow-warm-3 p-2",
        "animate-in fade-in slide-in-from-top-1 duration-150",
      )}
    >
      <p className="px-2 py-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
        Send {money}
      </p>
      <div className="flex flex-col gap-0.5">
        <ActionRow icon={ExternalLink} label="Venmo" onClick={() => openLink(venmoUrl)} />
        <ActionRow icon={ExternalLink} label="Bizum" onClick={() => openLink(bizumUrl)} />
        <ActionRow icon={ExternalLink} label="PayPal" onClick={() => openLink(paypalUrl)} />
        <div className="my-1 h-px bg-line" />
        <ActionRow
          icon={HandCoins}
          label="Record payment"
          onClick={() => {
            onClose();
            navigate(recordUrl);
          }}
        />
        <div className="my-1 h-px bg-line" />
        <ActionRow
          icon={Copy}
          label={`Copy amount (${money})`}
          onClick={() => {
            void copy(amount, "Amount");
            onClose();
          }}
        />
        <ActionRow
          icon={Copy}
          label="Copy note"
          onClick={() => {
            void copy(note, "Note");
            onClose();
          }}
        />
      </div>
    </div>
  );
}

function ActionRow({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Wallet;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-[14px] font-medium text-ink hover:bg-bg-sunken"
    >
      <Icon className="h-3.5 w-3.5 text-ink-subtle" />
      {label}
    </button>
  );
}
